import React, { useMemo, useState } from "react";
import InstructorLayout from "../../layouts/InstructorLayout";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import {
  useGetInstructorDemoSessionsQuery,
  useGetInstructorMySessionsQuery,
} from "../../services/instructor/instructor";

const getSessionViewData = (session) => {
  const sessionType =
    session?.enrollment?.enrollmentType?.toLowerCase() === "live classes"
      ? "live"
      : "demo";

  const sessionDateValue = session?.sessionDate
    ? session.sessionDate
    : session?.preferredDate
      ? new Date(session.preferredDate).toISOString().split("T")[0]
      : "";

  const sessionTimeValue =
    session?.startTime && session?.endTime
      ? session.startTime
      : session?.preferredTime || "";

  const studentNameValue =
    `${session?.student?.firstName || ""} ${session?.student?.lastName || ""}`.trim();

  const profileImageValue =
    session?.student?.profilePicture?.secureUrl || "";

  const typeValue =
    session?.enrollment?.enrollmentType?.toLowerCase() === "live classes"
      ? "Live Session"
      : "Demo Session";

  const displayDate = session?.sessionDate
    ? session.sessionDate.split("-").reverse().join("-")
    : session?.preferredDate
      ? new Date(session.preferredDate)
          .toLocaleDateString("en-GB", { timeZone: "UTC" })
          .replace(/\//g, "-")
      : "—";

  const displayTime =
    session?.startTime && session?.endTime
      ? `${session.startTime} - ${session.endTime}`
      : session?.preferredTime || "—";

  const typeBadgeClass =
    session?.enrollment?.enrollmentType?.toLowerCase() === "live classes"
      ? "bg-[#0E94B91A] text-[#0E94B9]"
      : "bg-[#0656231A] text-[#065623]";

  return {
    sessionType,
    sessionDateValue,
    sessionTimeValue,
    studentNameValue,
    profileImageValue,
    typeValue,
    displayDate,
    displayTime,
    typeBadgeClass,
  };
};

const ManageSessions = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("upcoming");

  const { data: sessionsResponse, isLoading } = useGetInstructorMySessionsQuery(
    {
      page: 1,
      limit: 10,
    },
  );
  const { data: demoSessionsResponse } = useGetInstructorDemoSessionsQuery();
  const filteredSessions = useMemo(() => {
    const sessions = sessionsResponse?.data || [];
    const demoSessions = demoSessionsResponse?.data || [];

    if (activeTab === "upcoming") {
      return sessions;
    }

    if (activeTab === "previous") {
      return demoSessions;
    }

    return sessions;
  }, [activeTab, sessionsResponse, demoSessionsResponse]);

  const openSessionDetails = (session, viewData) => {
    navigate(
      `/instructor/manage-sessions/${session?._id}?${new URLSearchParams({
        type: viewData.sessionType,
        sessionType: viewData.typeValue,
        date: viewData.sessionDateValue,
        time: viewData.sessionTimeValue,
        student: viewData.studentNameValue,
        studentImage: viewData.profileImageValue,
        preferredTime: session?.preferredTime || "",
        preferredDate: session?.preferredDate || "",
      }).toString()}`,
    );
  };

  return (
    <InstructorLayout>
      <div className="h-full overflow-x-hidden bg-[#D9D9D914] px-4 py-6 sm:px-8 sm:py-8 lg:px-14">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm sm:ml-1 sm:gap-4">
          <span className="text-[#6E7485]">Dashboard</span>
          <span className="text-[#6E7485]">/</span>
          <span className="text-[#1D2026]">Manage Sessions</span>
        </div>

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 sm:mb-6"
          aria-label="Go back"
        >
          <ChevronLeft size={30} className="text-[#089BC4]" />
        </button>

        <div className="w-full max-w-[1029px] bg-white px-4 pb-4 pt-3 shadow-[0_-1px_3.8px_0_#00000040] sm:px-6 lg:px-8">
          <div className="relative flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-[#D9D9D9] sm:gap-7">
            <button
              type="button"
              onClick={() => setActiveTab("upcoming")}
              className={`relative pb-3 text-base font-semibold leading-10 tracking-[-1%] sm:text-xl sm:leading-[48px] ${
                activeTab === "upcoming" ? "text-[#1D2026]" : "text-[#9CA3AF]"
              }`}
            >
              Upcoming Sessions
              {activeTab === "upcoming" ? (
                <span className="absolute bottom-0 left-0 h-[3px] w-full rounded-full bg-[#007AFF]" />
              ) : null}
            </button>

            {/* <button
              type="button"
              onClick={() => setActiveTab("previous")}
              className={`relative pb-3 text-base font-semibold leading-10 tracking-[-1%] sm:text-xl sm:leading-[48px] ${
                activeTab === "previous" ? "text-[#1D2026]" : "text-[#9CA3AF]"
              }`}
            >
              Previous Sessions
              {activeTab === "previous" ? (
                <span className="absolute bottom-0 left-0 h-[3px] w-full rounded-full bg-[#007AFF]" />
              ) : null}
            </button> */}
          </div>

          <div className="mt-5">
            <select className="w-full rounded-full border border-[#E9EAF0] px-4 py-1.5 text-sm leading-5 text-[#1D2026] focus:outline-none sm:w-auto">
              <option>Session Type</option>
              <option>Live Session</option>
              <option>Recorded</option>
            </select>
          </div>

          {/* Mobile cards */}
          <div className="mt-4 space-y-3 md:hidden">
            {isLoading ? (
              <p className="py-6 text-center text-sm text-[#6E7485]">
                Loading sessions...
              </p>
            ) : filteredSessions.length === 0 ? (
              <p className="py-6 text-center text-sm text-[#6E7485]">
                No sessions found.
              </p>
            ) : (
              filteredSessions.map((session) => {
                const viewData = getSessionViewData(session);

                return (
                  <div
                    key={`card-${session._id}`}
                    className="rounded-xl border border-[#E5E7EB] bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#111827]">
                          {viewData.displayDate}
                        </p>
                        <p className="text-sm text-[#6E7485]">
                          {viewData.displayTime}
                        </p>
                      </div>
                      <span
                        className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-medium ${viewData.typeBadgeClass}`}
                      >
                        {viewData.typeValue}
                      </span>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      {viewData.profileImageValue ? (
                        <img
                          src={viewData.profileImageValue}
                          alt={viewData.studentNameValue || "Student"}
                          className="h-7 w-7 shrink-0 rounded-full object-cover"
                        />
                      ) : null}
                      <span className="min-w-0 truncate text-sm text-[#111827]">
                        {viewData.studentNameValue || "—"}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-[#111827]">
                      <span>
                        {session?.sessionStatus
                          ? session.sessionStatus
                          : "1 of 1"}
                      </span>
                      <span className="text-[#D1D5DB]">•</span>
                      {activeTab === "upcoming" ? (
                        <span className="break-words">
                          {session?.course?.courseTitle || "—"}
                        </span>
                      ) : (
                        <span className="rounded-full bg-[#06CD5017] px-2 py-0.5 text-xs text-[#06CD50]">
                          Completed
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => openSessionDetails(session, viewData)}
                      className="mt-3 text-sm text-[#089BC4] hover:underline"
                    >
                      View
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Desktop / tablet table */}
          <div className="-mx-4 mt-4 hidden overflow-x-auto px-4 sm:mx-0 sm:px-0 md:block">
            <table className="min-w-[760px] w-full text-left font-['Inter']">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-xs font-semibold text-[#606161]">
                  <th className="whitespace-nowrap px-2 py-3">DATE &amp; TIME</th>
                  <th className="whitespace-nowrap px-2 py-3">STUDENT</th>
                  <th className="whitespace-nowrap px-2 py-3">SESSION</th>
                  <th className="whitespace-nowrap px-2 py-3">TYPE</th>
                  <th className="whitespace-nowrap px-2 py-3">
                    {activeTab === "upcoming" ? "COURSE" : "STATUS"}
                  </th>
                  <th className="whitespace-nowrap px-2 py-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td
                      colSpan={6}
                      className="py-6 text-center text-sm text-[#6E7485]"
                    >
                      Loading sessions...
                    </td>
                  </tr>
                )}

                {!isLoading && filteredSessions.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="py-6 text-center text-sm text-[#6E7485]"
                    >
                      No sessions found.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  filteredSessions.map((session) => {
                    const viewData = getSessionViewData(session);

                    return (
                      <tr
                        key={session._id}
                        className="border-b border-[#E5E7EB] last:border-0"
                      >
                        <td className="whitespace-nowrap px-2 py-3 text-sm text-[#111827]">
                          <div>{viewData.displayDate}</div>
                          <div>{viewData.displayTime}</div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-3">
                          <div className="flex items-center gap-2">
                            {viewData.profileImageValue ? (
                              <img
                                src={viewData.profileImageValue}
                                alt={viewData.studentNameValue || "Student"}
                                className="h-7 w-7 rounded-full object-cover"
                              />
                            ) : null}
                            <span className="max-w-[140px] truncate text-sm text-[#111827] lg:max-w-none">
                              {viewData.studentNameValue}
                            </span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-sm text-[#111827]">
                          {session?.sessionStatus
                            ? session.sessionStatus
                            : "1 of 1"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3">
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-medium ${viewData.typeBadgeClass}`}
                          >
                            {viewData.typeValue}
                          </span>
                        </td>
                        <td className="px-2 py-3">
                          <span
                            className={
                              activeTab === "upcoming"
                                ? "break-words text-sm text-[#111827]"
                                : "rounded-full bg-[#06CD5017] px-1 py-1 text-xs text-[#06CD50]"
                            }
                          >
                            {activeTab === "upcoming"
                              ? session?.course?.courseTitle
                              : "Completed"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-2 py-3">
                          <button
                            type="button"
                            onClick={() =>
                              openSessionDetails(session, viewData)
                            }
                            className="text-sm text-[#089BC4] hover:underline"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </InstructorLayout>
  );
};

export default ManageSessions;
